import { Devices, Facilities } from "@app/shared";
import { FC } from "react";
import { useFetch } from "../../../hooks";
import { InputRow, Select } from "../../shared/inputs";

// The org/location/device ids an alert subscription or recipient can be attached to.
// The server requires EXACTLY ONE of them to be set and rejects a payload with none
// (or two), so the UI has to state the scope explicitly rather than leave it implied.
export type AlertScope = {
    organizationId?: string;
    facilityId?: string;
    deviceId?: string;
};

export type AlertScopeKind = "organization" | "location" | "device";

export const scopeKindOf = (scope: AlertScope): AlertScopeKind => (scope.deviceId ? "device" : scope.facilityId ? "location" : "organization");

// Location/device names for the Scope column and the scope pickers. Both come from the
// same option endpoints the trigger and endpoint editors use, so ids never leak into
// the UI as truncated guids.
//
// `enabled` exists for the fleet-wide alerts view. These endpoints narrow by organization, and passing
// no organization means "every one the caller can see" — for a superadmin, every device in the system,
// pulled down to label a page of at most 200 alerts. That view reads the names off the alert rows
// instead, so it turns the fetches off rather than making them enormous.
export function useAlertScopeOptions(organizationId?: string, includeDevices: boolean = false, enabled: boolean = true) {
    const [facilityOptions, , facilityHelpers] = useFetch(() => (enabled ? Facilities.getOptions(organizationId) : Promise.resolve([])), [organizationId, enabled]);
    const [deviceOptions, , deviceHelpers] = useFetch(() => (enabled && includeDevices ? Devices.getOptions(organizationId) : Promise.resolve([])), [organizationId, includeDevices, enabled]);

    // Fall back to the id's own label text rather than the raw guid when an option is
    // missing (deleted location, device outside this org).
    const facilityLabel = (facilityId?: string): string => (facilityId ? facilityOptions?.find((o) => o.value === facilityId)?.label ?? "Unknown location" : "");
    const deviceLabel = (deviceId?: string): string => (deviceId ? deviceOptions?.find((o) => o.value === deviceId)?.label ?? "Unknown device" : "");

    const scopeLabel = (scope: AlertScope): string => {
        if (scope.deviceId) return deviceLabel(scope.deviceId);
        if (scope.facilityId) return facilityLabel(scope.facilityId);
        return "Entire organization";
    };

    return { facilityOptions, deviceOptions, facilityLabel, deviceLabel, scopeLabel, isLoading: facilityHelpers.isLoading || deviceHelpers.isLoading };
}

type AlertScopeInputsProps = {
    organizationId?: string;
    scope: AlertScope;
    /** Subscriptions can target a single device; free-form recipients can't. */
    allowDevice?: boolean;
    onChange: (scope: AlertScope) => void;
};

export const AlertScopeInputs: FC<AlertScopeInputsProps> = ({ organizationId, scope, allowDevice = false, onChange }) => {
    const { facilityOptions, deviceOptions, isLoading } = useAlertScopeOptions(organizationId, allowDevice);
    const kind = scopeKindOf(scope);
    const locations = facilityOptions ?? [];
    const devices = deviceOptions ?? [];

    // Switching kind has to set the new id in the SAME change. The kind is derived from
    // which id is set, so clearing all three and waiting for the follow-up select to fill
    // one in makes the dropdown snap straight back to "organization" — the other choices
    // read as unselectable. A choice with nothing to point at is disabled instead.
    const changeKind = (next: AlertScopeKind) => {
        if (next === "location") onChange({ facilityId: locations[0]?.value });
        else if (next === "device") onChange({ deviceId: devices[0]?.value });
        else onChange({ organizationId });
    };

    return (
        <>
            <InputRow>
                <Select
                    label="Alert me about"
                    value={kind}
                    onChange={(e) => changeKind(e.target.value as AlertScopeKind)}
                    options={[
                        { value: "organization", label: "Everything in this organization" },
                        {
                            value: "location",
                            label: locations.length ? "A single location" : "A single location (none available)",
                            disabled: !locations.length,
                        },
                        ...(allowDevice
                            ? [
                                  {
                                      value: "device",
                                      label: devices.length ? "A single device" : "A single device (none available)",
                                      disabled: !devices.length,
                                  },
                              ]
                            : []),
                    ]}
                />
            </InputRow>
            {kind === "location" && (
                <InputRow>
                    <Select
                        label="Location"
                        isLoadingOptions={isLoading}
                        options={facilityOptions}
                        value={scope.facilityId ?? ""}
                        onChange={(e) => onChange({ facilityId: e.target.value || undefined })}
                    />
                </InputRow>
            )}
            {kind === "device" && (
                <InputRow>
                    <Select
                        label="Device"
                        isLoadingOptions={isLoading}
                        options={deviceOptions}
                        value={scope.deviceId ?? ""}
                        onChange={(e) => onChange({ deviceId: e.target.value || undefined })}
                    />
                </InputRow>
            )}
        </>
    );
};
